import React from 'react'
import type { Editor } from '@tiptap/react'
import { FloatingMenu } from '@tiptap/react'
import type { Props as TippyProps } from 'tippy.js'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'

export interface EditorFloatingMenuProps {
  editor: Editor
  noDisplayClassName?: string
  tippyOptions?: Partial<TippyProps>
}

const useStyles = makeStyles<Theme>((theme) => ({
  tippyMenu: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
    padding: 0,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
    display: 'flex',
    flexWrap: 'wrap',
    '& button': {
      background: 'none',
      border: 'none',
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(16),
      minWidth: theme.typography.pxToRem(30),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(0.5),
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.paintscout.palette.brand.Neutral.P50,
      cursor: 'pointer',
      '& svg': {
        fontSize: '1rem'
      },
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P40
      },
      '&.is-active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P85
      }
    }
  }
}))

// Fixes disappearing menu on click w/ Safari Browser
const handleMouseDown = (ev: React.MouseEvent<HTMLButtonElement>) => {
  ev.preventDefault()
}

export function EditorFloatingMenu({ editor, noDisplayClassName, tippyOptions }: EditorFloatingMenuProps) {
  const classes = useStyles()

  return (
    <div className={noDisplayClassName}>
      <FloatingMenu editor={editor} tippyOptions={{ zIndex: 4, ...tippyOptions }}>
        <div id="floating-menu" className={classes.tippyMenu}>
          <button
            type="button"
            onMouseDown={handleMouseDown}
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().toggleBulletList().run()
            }}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
          >
            <FormatListBulletedIcon />
          </button>
          <button
            type="button"
            onMouseDown={handleMouseDown}
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().toggleOrderedList().run()
            }}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
          >
            <FormatListNumberedIcon />
          </button>
        </div>
      </FloatingMenu>
    </div>
  )
}
